import * as DialogPrimitive from '@radix-ui/react-dialog'
import { X } from 'lucide-react'

import * as React from 'react'

import { usePortalContainer } from './portal'
import { cn } from './utils'

const Dialog = DialogPrimitive.Root

const DialogTrigger = DialogPrimitive.Trigger

const DialogPortal = DialogPrimitive.Portal

const DialogClose = DialogPrimitive.Close

const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    className={cn(
      'fixed inset-0 z-50 bg-black/50 data-[state=open]:animate-in data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=open]:fade-in-0',
      className
    )}
    {...props}
  />
))
DialogOverlay.displayName = DialogPrimitive.Overlay.displayName

const dialogSizes = {
  sm: 'sm:max-w-[420px]',
  md: 'sm:max-w-[620px]',
  lg: 'sm:max-w-[800px]',
  xl: 'sm:max-w-[1200px]',
  full: 'sm:max-w-[calc(100vw-64px)]',
} as const

export type DialogSize = keyof typeof dialogSizes

/**
 * Centered dialog. The content scrolls; DialogHeader and DialogFooter stick to its top and bottom so the title
 * and actions stay visible in long forms.
 */
const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
    size?: DialogSize
    /** Drop the corner × when the content renders its own close control (media picker). */
    hideClose?: boolean
  }
>(({ className, children, size = 'md', hideClose, ...props }, ref) => {
  const container = usePortalContainer()
  return (
  <DialogPortal container={container}>
    <DialogOverlay />
    <DialogPrimitive.Content
      ref={ref}
      className={cn(
        'group/dialog fixed left-[50%] top-[50%] z-50 grid w-[calc(100%-32px)] gap-4 rounded-xl bg-card p-6 shadow-dialog outline-none',
        dialogSizes[size],
        'translate-x-[-50%] translate-y-[-50%]',
        'max-h-[90vh] overflow-y-auto scrollbar-hide',
        'opacity-0 scale-95',
        'transition-all duration-200 ease-out',
        'data-[state=open]:opacity-100 data-[state=open]:scale-100',
        className
      )}
      {...props}
    >
      {children}
      {!hideClose && (
        <DialogPrimitive.Close className="absolute right-4 top-4 z-20 grid size-7 place-items-center rounded-full text-muted-foreground transition-colors hover:bg-fill hover:text-ink focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:pointer-events-none">
          <X className="size-5" />
          <span className="sr-only">Close</span>
        </DialogPrimitive.Close>
      )}
    </DialogPrimitive.Content>
  </DialogPortal>
)
})
DialogContent.displayName = DialogPrimitive.Content.displayName

const DialogHeader = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'sticky -top-6 z-10 -mx-6 -mt-6 flex flex-col gap-1 bg-card px-6 pb-2 pr-14 pt-6 text-left',
      // Custom-layout dialogs (p-0) place their own header.
      'group-[.p-0]/dialog:static group-[.p-0]/dialog:m-0',
      className
    )}
    {...props}
  />
)
DialogHeader.displayName = 'DialogHeader'

const DialogFooter = ({ className, ...props }: React.HTMLAttributes<HTMLDivElement>) => (
  <div
    className={cn(
      'sticky -bottom-6 z-10 -mx-6 -mb-6 flex flex-col-reverse gap-2 border-t border-border bg-card px-6 pb-6 pt-4 sm:flex-row sm:justify-end',
      'group-[.p-0]/dialog:static group-[.p-0]/dialog:m-0',
      className
    )}
    {...props}
  />
)
DialogFooter.displayName = 'DialogFooter'

const DialogTitle = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Title>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Title>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Title
    ref={ref}
    className={cn('text-heading-md text-ink', className)}
    {...props}
  />
))
DialogTitle.displayName = DialogPrimitive.Title.displayName

const DialogDescription = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Description>,
  React.ComponentPropsWithoutRef<typeof DialogPrimitive.Description>
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Description
    ref={ref}
    className={cn('text-sm text-muted-foreground', className)}
    {...props}
  />
))
DialogDescription.displayName = DialogPrimitive.Description.displayName

export {
  Dialog,
  DialogPortal,
  DialogOverlay,
  DialogClose,
  DialogTrigger,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
  DialogDescription,
}
